import { AuditLogEntry } from "../../models/auditLog";

export type QuantityChangeRow = {
	id: string;
	timestamp: string;
	entityType: string;
	operation: string;
	username: string;
	itemCode?: string;
	itemName?: string;
	invoiceNumber?: string;
	beforeQuantity: number | null;
	afterQuantity: number | null;
	changeQuantity: number | null;
};

const toNumber = (value: unknown): number | null => {
	if (typeof value === "number" && Number.isFinite(value)) {
		return value;
	}

	if (typeof value === "string" && value.trim() !== "") {
		const parsed = Number(value);
		return Number.isFinite(parsed) ? parsed : null;
	}

	return null;
};

export const buildQuantityChangeRows = (
	auditLogs: AuditLogEntry[],
	selectedItemCode?: string
): QuantityChangeRow[] => {
	const itemCodeFilter = selectedItemCode?.toString();
	return auditLogs.flatMap((log) => {
		if (log.entityType === "Invoice") {
			const soldItems =
				(log.metadata?.soldItems as
					| Array<{
							itemCode?: string;
							description?: string;
							itemName?: string;
							quantity?: number;
					  }>
					| undefined) ?? [];
			return soldItems
				.map((item, index) => {
					const code = item.itemCode ?? log.metadata?.itemCode;
					if (itemCodeFilter && code !== itemCodeFilter) return null;

					// Try to find a matching change entry for this sold item
					const changeKey = Object.keys(log.changes ?? {}).find(
						(k) =>
							k
								.toLowerCase()
								.includes((code ?? "").toLowerCase()) &&
							k.toLowerCase().startsWith("solditem:")
					);

					const changeEntry = changeKey
						? (log.changes as any)[changeKey]
						: undefined;
					const before = changeEntry
						? toNumber(
								changeEntry.before?.availableQuantity ??
									changeEntry.before?.availableQuantity
							)
						: null;
					const after = changeEntry
						? toNumber(
								changeEntry.after?.availableQuantity ??
									changeEntry.after?.availableQuantity
							)
						: null;

					return {
						id: `${log.id}-invoice-${index}`,
						timestamp: log.timestamp,
						entityType: log.entityType,
						operation: log.operation,
						username: log.username,
						itemCode: code,
						invoiceNumber: log.metadata?.invoiceNumber,
						itemName:
							item.description ??
							item.itemName ??
							log.metadata?.itemName,
						beforeQuantity: before,
						afterQuantity: after,
						changeQuantity:
							after !== null && before !== null
								? after - before
								: null
					} as QuantityChangeRow;
				})
				.filter(Boolean) as QuantityChangeRow[];
		}

		if (log.entityType === "Return") {
			const returnedItems =
				(log.metadata?.returnedItems as
					| Array<{
							itemCode?: string;
							itemName?: string;
							quantity?: number;
					  }>
					| undefined) ?? [];
			return returnedItems
				.map((item, index) => {
					const code = item.itemCode ?? log.metadata?.itemCode;
					if (itemCodeFilter && code !== itemCodeFilter) return null;

					const changeKey = Object.keys(log.changes ?? {}).find(
						(k) =>
							k
								.toLowerCase()
								.includes((code ?? "").toLowerCase()) &&
							k.toLowerCase().startsWith("returneditem:")
					);

					const changeEntry = changeKey
						? (log.changes as any)[changeKey]
						: undefined;
					const before = changeEntry
						? toNumber(changeEntry.before?.availableQuantity)
						: null;
					const after = changeEntry
						? toNumber(changeEntry.after?.availableQuantity)
						: null;

					return {
						id: `${log.id}-return-${index}`,
						timestamp: log.timestamp,
						entityType: log.entityType,
						operation: log.operation,
						username: log.username,
						itemCode: code,
						invoiceNumber: log.metadata?.invoiceNumber,
						itemName: item.itemName ?? log.metadata?.itemName,
						beforeQuantity: before,
						afterQuantity: after,
						changeQuantity:
							after !== null && before !== null
								? after - before
								: null
					} as QuantityChangeRow;
				})
				.filter(Boolean) as QuantityChangeRow[];
		}

		if (log.entityType === "Item") {
			const rows: QuantityChangeRow[] = [];

			Object.entries(log.changes ?? {}).forEach(
				([field, diff], index) => {
					const before = diff
						? toNumber((diff as { before?: unknown }).before)
						: null;
					const after = diff
						? toNumber((diff as { after?: unknown }).after)
						: null;

					const isQuantityField =
						field.toLowerCase().includes("quantity") ||
						field.toLowerCase().includes("qty");

					if (
						!isQuantityField ||
						(before === null && after === null)
					) {
						return;
					}

					// If selected item code is provided, ignore entries for other items
					if (
						itemCodeFilter &&
						log.metadata?.itemCode &&
						log.metadata.itemCode !== itemCodeFilter
					) {
						return;
					}

					// For Item entity creations there may be no "before" recorded.
					// Treat missing before as 0 for change calculation per requirement.
					const beforeVal = before === null ? 0 : before;
					rows.push({
						id: `${log.id}-item-${index}`,
						timestamp: log.timestamp,
						entityType: log.entityType,
						operation: log.operation,
						username: log.username,
						itemCode: log.metadata?.itemCode,
						itemName: log.metadata?.itemName,
						beforeQuantity: beforeVal,
						afterQuantity: after,
						changeQuantity:
							after !== null ? after - beforeVal : null
					});
				}
			);

			return rows;
		}

		return [];
	});
};

export default buildQuantityChangeRows;
